import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Sparkles, Sprout } from 'lucide-react';

// Public-domain traditional iconography from Wikimedia Commons
const GURU_NANAK_IMG =
  'https://upload.wikimedia.org/wikipedia/commons/4/4c/Mural_painting_of_Guru_Nanak_from_Gurdwara_Baba_Atal_Rai.jpg';
const BEBE_NANAKI_IMG =
  'https://upload.wikimedia.org/wikipedia/commons/e/e9/Guru_Nanak_visits_Bibi_Nanaki.jpg';

// Inline Khanda SVG — Sikh emblem (women + community symbolism)
const KhandaSVG = ({ className = '' }) => (
  <svg viewBox="0 0 64 64" className={className} xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M32 4c-1 0-2 .8-2 1.8 0 4.5-2.6 8.4-6.4 10.4-1 .5-1.4 1.7-.9 2.7.5 1 1.7 1.4 2.7.9 2-1 3.7-2.5 5-4.3v34.6c-2.5-1-4.4-3.4-4.4-6.3 0-.4-.1-.8-.3-1.1L18 32l7.7-10.7c.6-.9.4-2.1-.5-2.7-.9-.6-2.1-.4-2.7.5L14 31.5c-.5.7-.5 1.6 0 2.3l8.5 11.6c.3.4.7.7 1.2.8.4 2.6 2.4 4.7 4.9 5.5C26 53 22 56.6 22 60v.7c0 .7.6 1.3 1.3 1.3h17.4c.7 0 1.3-.6 1.3-1.3V60c0-3.4-4-7-6.6-8.3 2.5-.8 4.5-2.9 4.9-5.5.5-.1.9-.4 1.2-.8L50 33.8c.5-.7.5-1.6 0-2.3l-8.5-11.4c-.6-.9-1.8-1.1-2.7-.5-.9.6-1.1 1.8-.5 2.7L46 32l-7.7 10.7c-.2.3-.3.7-.3 1.1 0 2.9-1.9 5.3-4.4 6.3V15.5c1.3 1.8 3 3.3 5 4.3 1 .5 2.2.1 2.7-.9.5-1 .1-2.2-.9-2.7-3.8-2-6.4-5.9-6.4-10.4 0-1-.9-1.8-2-1.8z" />
  </svg>
);

// Inline Ik Onkar (ੴ) glyph rendered as text — feels native to the typography
const IkOnkar = ({ className = '' }) => (
  <span className={className} style={{ fontFamily: 'serif' }}>
    ੴ
  </span>
);

const OurPhilosophy = () => {
  return (
    <div className="bg-[#F9F8F6] text-stone-800" data-testid="our-philosophy-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FBF7F2] via-[#F4ECE2] to-[#F9F8F6]">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#4A3043 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <IkOnkar className="text-5xl md:text-6xl text-[#B6594C] block mb-4 leading-none" />
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-stone-500 mb-6">
            Our Philosophy
          </span>
          <h1 className="font-serif text-5xl md:text-7xl leading-tight text-[#2A1B2D] mb-8" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
            Commerce as a tool for<br className="hidden md:block" />{' '}
            <em className="text-[#B6594C]">empowerment</em>, dignity,
            <br className="hidden md:block" />{' '}and collective upliftment.
          </h1>
          <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
            Nanaki is built on the spirit of Bebe Nanaki Ji — the first to recognize the divine light in Guru Nanak Dev Ji.
            We carry forward that same belief: in women, in community, in the power of collective growth.
          </p>
          <div className="mt-12 flex justify-center">
            <KhandaSVG className="w-12 h-12 text-[#B6594C]/70" />
          </div>
        </div>
      </section>

      {/* INSPIRATION — BEBE NANAKI */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 md:order-1">
              <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
                The Inspiration
              </span>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D] mb-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                Bebe Nanaki Ji
              </h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                The foundation of Nanaki is inspired by <strong>Bebe Nanaki Ji</strong>, the elder sister of
                Guru Nanak Dev Ji, often remembered as the first person to truly recognize the divine light within him.
              </p>
              <p className="text-stone-600 leading-relaxed mb-4">
                Before the world understood his vision, she saw it with faith, love, and spiritual clarity.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Nanaki was created to carry forward that same spirit of belief — belief in women,
                belief in community, and belief in the power of collective growth.
              </p>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#F4ECE2] rounded-2xl rotate-2" />
                <img
                  src={BEBE_NANAKI_IMG}
                  alt="Guru Nanak Dev Ji visiting his elder sister Bebe Nanaki Ji — traditional Sikh artwork"
                  className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/5]"
                  loading="lazy"
                />
                <p className="text-xs text-stone-400 italic mt-3 text-center">
                  Guru Nanak visits Bebe Nanaki Ji — traditional artwork
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GURU NANAK DEV JI + QUOTE */}
      <section className="py-20 md:py-28 bg-[#FBF7F2] relative overflow-hidden">
        <KhandaSVG className="absolute -right-12 -top-12 w-64 h-64 text-[#B6594C]/[0.04]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E8DCC8] rounded-2xl -rotate-2" />
                <img
                  src={GURU_NANAK_IMG}
                  alt="Mural painting of Guru Nanak Dev Ji from Gurdwara Baba Atal Rai"
                  className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/5]"
                  loading="lazy"
                />
                <p className="text-xs text-stone-400 italic mt-3 text-center">
                  Guru Nanak Dev Ji — mural at Gurdwara Baba Atal Rai
                </p>
              </div>
            </div>
            <div>
              <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
                A Modern Ecosystem · Rooted in Sikh Values
              </span>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D] mb-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                Equality, honest living,<br /> shared humanity.
              </h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                At a time when women across much of the world were denied equal status,
                Guru Nanak Dev Ji openly challenged discrimination and spoke about the spiritual
                and social dignity of women.
              </p>
              <blockquote className="border-l-4 border-[#B6594C] pl-6 my-8">
                <p className="font-serif text-2xl md:text-3xl italic text-[#2A1B2D] leading-snug" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                  &ldquo;Why call her inferior, from whom kings are born?&rdquo;
                </p>
                <footer className="text-sm text-stone-500 mt-3 not-italic">
                  — Guru Nanak Dev Ji, Asa Di Var
                </footer>
              </blockquote>
              <p className="text-stone-600 leading-relaxed">
                This philosophy established that women are not secondary participants in society,
                but <strong>equal contributors</strong> to spirituality, leadership, family, labor,
                and community building.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE MODEL — How it works */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
              The Nanaki Affiliate Empowerment Model
            </span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D] max-w-3xl mx-auto" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
              A non-profit enabled commerce<br className="hidden sm:block" />{' '}&amp; empowerment network.
            </h2>
            <p className="text-stone-600 mt-6 max-w-3xl mx-auto leading-relaxed">
              Brands, creators, influencers, artisans and communities collaborate to generate shared economic opportunity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Brands */}
            <div className="bg-[#FBF7F2] border border-[#E8DCC8] rounded-2xl p-8">
              <div className="w-12 h-12 rounded-full bg-[#B6594C]/10 text-[#B6594C] flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-2xl mb-4 text-[#2A1B2D]" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                For partner brands
              </h3>
              <ul className="space-y-3 text-stone-600">
                {[
                  'Ethical storytelling and social visibility',
                  'Influencer-driven promotion',
                  'Access to women-led creator communities',
                  'Community-centered marketing campaigns',
                  'A socially conscious brand identity aligned with empowerment'
                ].map((it) => (
                  <li key={it} className="flex items-start">
                    <span className="text-[#B6594C] mr-3 mt-1">◆</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Affiliates */}
            <div className="bg-[#4A3043] text-white rounded-2xl p-8">
              <div className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center mb-6">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-2xl mb-4" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                For affiliates &amp; creators
              </h3>
              <p className="text-white/80 mb-4 leading-relaxed">
                Women creators, influencers, artists, homemakers, students, and entrepreneurs earn commissions by:
              </p>
              <ul className="space-y-3 text-white/90">
                {[
                  'Promoting partnered brands',
                  'Generating sales through personalized links or storefronts',
                  'Creating authentic content and recommendations',
                  'Building communities around fashion, wellness, art, spirituality, sustainability'
                ].map((it) => (
                  <li key={it} className="flex items-start">
                    <span className="text-[#E8DCC8] mr-3 mt-1">◆</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-center text-stone-600 italic mt-12 max-w-3xl mx-auto leading-relaxed">
            This transforms influencer marketing from a purely commercial activity into a model of <strong>distributed economic empowerment</strong> —
            flowing toward everyday women, including homemakers, students, artists, mothers, rural creators, and first-time entrepreneurs.
          </p>
        </div>
      </section>

      {/* TRADE WITH PURPOSE */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#F4ECE2] to-[#FBF7F2]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sprout className="w-10 h-10 text-[#B6594C] mx-auto mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D] mb-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Trade with purpose.
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto leading-relaxed mb-12">
            Every transaction can create impact. A product sold through Nanaki is not simply a sale —
            it becomes:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {[
              { t: 'Income', d: 'for a creator' },
              { t: 'Exposure', d: 'for a women-led brand' },
              { t: 'Livelihood', d: 'for a family' },
              { t: 'Visibility', d: 'for ethical businesses' },
              { t: 'Community', d: 'support for empowerment' }
            ].map((p) => (
              <div key={p.t} className="bg-white rounded-xl p-5 border border-[#E8DCC8] shadow-sm">
                <p className="font-serif text-2xl text-[#B6594C] mb-1" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                  {p.t}
                </p>
                <p className="text-xs text-stone-500">{p.d}</p>
              </div>
            ))}
          </div>
          <p className="text-stone-600 mt-12 max-w-2xl mx-auto italic leading-relaxed">
            Through the support of <strong>Drishty Foundation</strong> and aligned social initiatives,
            the ecosystem continuously reinvests into education, skill development, mentorship,
            creative opportunities, and women-led growth programs.
          </p>
        </div>
      </section>

      {/* DIGITAL SISTERHOOD */}
      <section className="py-20 md:py-28 bg-[#2A1B2D] text-white relative overflow-hidden">
        <KhandaSVG className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] text-white/[0.03]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#E8DCC8] mb-4">
            A Digital Sisterhood
          </span>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Rooted in timeless values,<br /> built for the modern world.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto leading-relaxed mb-12">
            Four principles that guide every decision we make at Nanaki:
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { t: 'Seva', d: 'Selfless service to the community' },
              { t: 'Kirat Karo', d: 'Honest, dignified earning' },
              { t: 'Vand Chhako', d: 'Sharing prosperity with others' },
              { t: 'Equality', d: 'Beyond gender, class, or background' }
            ].map((v) => (
              <div key={v.t} className="border border-white/15 rounded-2xl p-6 backdrop-blur hover:bg-white/5 transition">
                <p className="font-serif text-3xl text-[#E8DCC8] mb-2" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                  {v.t}
                </p>
                <p className="text-sm text-white/70">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-10 h-10 text-[#B6594C] mx-auto mb-8" />
          <p className="font-serif text-2xl md:text-3xl text-[#2A1B2D] leading-relaxed mb-8" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Nanaki is a modern interpretation of what Bebe Nanaki Ji represented centuries ago —<br />
            <em className="text-[#B6594C]">the power of recognizing light in others before the world sees it.</em>
          </p>
          <p className="text-stone-600 leading-relaxed">
            Because true empowerment is not built through charity alone.
            It is built when communities create systems where <strong>dignity, ownership, income, and opportunity</strong> can be shared by all.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register-angel"
              className="inline-flex items-center justify-center bg-[#B6594C] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#9d4a40] transition"
              data-testid="philosophy-cta-angel"
            >
              Become a Nanaki&apos;s Kaur
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/showcase-brand"
              className="inline-flex items-center justify-center border border-[#2A1B2D] text-[#2A1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#FBF7F2] transition"
              data-testid="philosophy-cta-brand"
            >
              Showcase your brand
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurPhilosophy;
